import React from 'react';
import { Typography } from 'antd';
import { ResponsiveSelect } from '../../ui/inputs/responsive-select';
import { FormFieldRenderer } from '../form-field-renderer';
import { CustomTooltip } from './custom-tooltip';
import type { BaseFieldProps } from '../../../types/form-renderer';
import type {
  PolymorphicField as PolymorphicFieldType,
  PolymorphicSubValue,
  PolymorphicValue,
  RoutedFormData,
  FormDataValue
} from '../../../types/templates';
import type { CreativityType } from '../../../types/banner';

const { Text } = Typography;

type Props = BaseFieldProps<PolymorphicFieldType> & {
  formData: RoutedFormData;
  errors: Record<string, string>;
  creativityType: CreativityType;
};

const readValue = (raw: PolymorphicValue | undefined, defaultVariant: string): PolymorphicValue => {
  if (raw && typeof raw === 'object') return { variant: raw.variant ?? defaultVariant, values: raw.values ?? {} };
  return { variant: defaultVariant, values: {} };
};

export const PolymorphicField: React.FC<Props> = ({
  field,
  value,
  onChange,
  commonStyle,
  labelStyle,
  error,
  formData,
  errors,
  creativityType
}) => {
  const current = readValue(value, field.defaultVariant);
  const activeVariant = field.variants.find(v => v.key === current.variant) ?? field.variants[0];

  const variantOptions = field.variants.map(v => ({ value: v.key, label: v.label }));

  const handleVariantChange = (nextVariant: string) => {
    // Fill new sub-fields with their defaultValue; keep whatever the user already typed.
    const next = field.variants.find(v => v.key === nextVariant);
    const seeded = { ...current.values };
    next?.fields.forEach(sub => {
      if (seeded[sub.key] === undefined && 'defaultValue' in sub && sub.defaultValue !== undefined) {
        seeded[sub.key] = sub.defaultValue;
      }
    });
    onChange({ variant: nextVariant, values: seeded });
  };

  // Sub-fields' onChange writes back into values[subKey].
  const subFormData = { ...current.values } as RoutedFormData;
  const handleSubFieldChange = async (subKey: string, subValue: FormDataValue): Promise<RoutedFormData> => {
    const nextValues = { ...current.values, [subKey]: subValue as PolymorphicSubValue };
    onChange({ variant: current.variant, values: nextValues });
    return nextValues as RoutedFormData;
  };

  return (
    <div style={{ ...commonStyle, marginBottom: 0 }}>
      <Text type='secondary' style={{ ...labelStyle, textTransform: 'uppercase' }}>
        {field.label}
      </Text>

      <CustomTooltip field={field}>
        <ResponsiveSelect
          value={current.variant}
          onChange={handleVariantChange}
          options={variantOptions}
          showSearch={false}
          style={{ width: '100%', textAlign: 'left', marginBottom: 12 }}
        />
      </CustomTooltip>

      {activeVariant.fields.map(subField => (
        <FormFieldRenderer
          key={subField.key}
          field={subField}
          formData={subFormData}
          onFieldChange={handleSubFieldChange}
          errors={errors}
          creativityType={creativityType}
        />
      ))}

      {error && (
        <Text type='danger' style={{ fontSize: 12, marginTop: 4 }}>
          {error}
        </Text>
      )}
    </div>
  );
};
